import React, { memo, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Accordion } from 'react-bootstrap';
import HomePageCardStyled from './HomePageCardStyled';
import { REPORT_TITLE, REPORT_STATUS } from '../../../Common/Constants/digitalcare';
import ReportProgress from '../ReportProgress';
import LabReports from '../LabReports';
import { labReportCardExpand } from '../LabReportAnalytics';

const HomePageCard = ({ report, total, completed, inProgress }) => {
  const isSingleUser = useMemo(() => report?.length === 1, [report]);
  const patients = useMemo(() => report?.map((patient) => patient?.firstName), [report]);
  const isReportInProgress = useMemo(
    () => (isSingleUser ? inProgress > 0 : total !== completed),
    [isSingleUser, inProgress, total, completed],
  );
  const allCompletedCount = useMemo(
    () => (isSingleUser && total === completed ? total : 0),
    [isSingleUser, total, completed],
  );
  const [accordionState, setAccordionState] = useState({ borderlessHeader: false, isExpanded: false });

  const handleHeaderClick = () => {
    setAccordionState((prevState) => ({
      ...prevState,
      borderlessHeader: !prevState.borderlessHeader,
      isExpanded: !prevState.isExpanded,
    }));
    labReportCardExpand(report, accordionState.isExpanded ? 'collapse' : 'expand');
  };

  const renderCount = () => (
    <div className="report_count">
      <span>
        {inProgress} {REPORT_STATUS.PROGRESS}
      </span>
      <span className="divider" />
      <span>
        {completed} {REPORT_STATUS.COMPLETED}
      </span>
    </div>
  );

  const renderTitle = (patientList) => {
    const isSinglePatient = patientList?.length === 1;
    return (
      <div className="report_status_wrapper">
        <div className="report_status">
          <span>{REPORT_TITLE.TITLE}&nbsp;</span>
          {isReportInProgress ? REPORT_TITLE.PROGRESS : REPORT_TITLE.READY}
        </div>
        {isSinglePatient &&
          (isReportInProgress ? (
            renderCount()
          ) : (
            <span className="completed_count">
              {allCompletedCount} {REPORT_STATUS.COMPLETED}
            </span>
          ))}
        {!isSinglePatient && (
          <div className="report_count">
            <span className="completed_count">
              for {patientList?.length} patients: {patientList?.join(', ')}
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderHeader = () => (
    <div className="report_header">
      {isSingleUser && (
        <div className="report_progress">
          <ReportProgress
            singleUser={isSingleUser}
            completedCount={completed}
            totalCount={total}
            isInProgress={isReportInProgress}
          />
        </div>
      )}
      {!isSingleUser && !isReportInProgress && allCompletedCount > 0 && (
        <div className="report_progress">
          <ReportProgress
            singleUser={isSingleUser}
            completedCount={completed}
            totalCount={total}
            isInProgress={false}
          />
        </div>
      )}
      <div className="report_title">{renderTitle(patients)}</div>
    </div>
  );

  return (
    <HomePageCardStyled>
      <Accordion>
        <Accordion.Item eventKey="0" style={accordionState.borderlessHeader ? { border: 'none' } : {}}>
          <Accordion.Header onClick={handleHeaderClick}>{renderHeader()}</Accordion.Header>
          <Accordion.Body>
            <LabReports labReport={report} />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </HomePageCardStyled>
  );
};

HomePageCard.propTypes = {
  completed: PropTypes.number,
  inProgress: PropTypes.number,
  report: PropTypes.arrayOf({}),
  total: PropTypes.number,
};

HomePageCard.defaultProps = {
  report: [],
  total: 0,
  completed: 0,
  inProgress: 0,
};

export default memo(HomePageCard);
